
// =====================================================
// client/src/services/messagesApi.js
// =====================================================
// API service for messaging functionality including:
// - Thread management (create, read, list)
// - Message sending and reading
// - Task mention notifications
// - Push notification subscriptions
// =====================================================

import axios from 'axios';
import { getSessionLocal } from './sessionLocal';

const baseUrl = process.env.REACT_APP_BASE_URL || '';

// =====================================================
// Context Helpers
// =====================================================

/**
 * Get current user context from session/localStorage
 * @param {object} options - Override options for userId/tenantId
 * @returns {object} { userId, tenantId }
 */
function getUserContext(options = {}) {
  const session = getSessionLocal();
  
  // Try from options first, then session, then localStorage
  let userId = options.userId || session?.userId || session?.userid;
  let tenantId = options.tenantId || session?.tenantId || session?.tenantid;
  
  // Fallback to localStorage 'me' object
  if (!userId) {
    try {
      const me = JSON.parse(localStorage.getItem('me') || '{}');
      userId = me?.id || me?.userid || me?.userId;
    } catch {
      // Ignore parse errors
    }
  }
  
  return {
    userId: userId ? Number(userId) : null,
    tenantId: tenantId ? Number(tenantId) : null,
  };
}

/**
 * Build headers with user and tenant context
 * @param {number} userId - User ID
 * @param {number} tenantId - Tenant ID
 * @returns {object} Headers object
 */
function buildHeaders(userId, tenantId) {
  return {
    'Content-Type': 'application/json',
    'X-User-Id': userId ? String(userId) : '',
    'X-Tenant-Id': tenantId ? String(tenantId) : '',
  };
}

// =====================================================
// Thread API
// =====================================================

/**
 * Get all message threads for current user
 * @param {object} options - Optional userId/tenantId overrides
 * @returns {Promise<object>} { threads: [...] }
 */
export async function getThreads(options = {}) {
  const { userId, tenantId } = getUserContext(options);
  
  if (!userId || !tenantId) {
    console.warn('getThreads: Missing userId or tenantId');
    return { threads: [] };
  }

  try {
    const response = await axios.get(`${baseUrl}/api/messages/threads`, {
      headers: buildHeaders(userId, tenantId),
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    console.error('getThreads failed:', error?.response?.data || error.message);
    throw error;
  }
}

/**
 * Get a specific thread with its messages
 * @param {number} threadId - Thread ID
 * @param {object} options - Optional userId/tenantId overrides
 * @returns {Promise<object>} Thread object with messages
 */
export async function getThread(threadId, options = {}) {
  const { userId, tenantId } = getUserContext(options);
  
  if (!userId || !tenantId || !threadId) {
    console.warn('getThread: Missing required parameters');
    return { thread: null };
  }

  try {
    const response = await axios.get(`${baseUrl}/api/messages/threads/${threadId}`, {
      headers: buildHeaders(userId, tenantId),
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    console.error('getThread failed:', error?.response?.data || error.message);
    throw error;
  }
}

/**
 * Create a new thread with specified participants
 * @param {number[]} participantIds - Array of user IDs to include
 * @param {object} options - Optional title and context
 * @returns {Promise<object>} Created thread object
 */
export async function createThread(participantIds, options = {}) {
  const { userId, tenantId } = getUserContext(options);
  
  if (!userId || !tenantId || !participantIds || participantIds.length === 0) {
    console.warn('createThread: Missing required parameters');
    return null;
  }

  try {
    const response = await axios.post(
      `${baseUrl}/api/messages/threads`,
      { 
        participantIds,
        title: options.title || null,
      },
      {
        headers: buildHeaders(userId, tenantId),
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error) {
    console.error('createThread failed:', error?.response?.data || error.message);
    throw error;
  }
}

// =====================================================
// Message API
// =====================================================

/**
 * Send a message to a thread
 * @param {number} threadId - Thread ID
 * @param {string} text - Message text
 * @param {object} options - Optional context
 * @returns {Promise<object>} Sent message object
 */
export async function sendMessage(threadId, text, options = {}) {
  const { userId, tenantId } = getUserContext(options);
  
  if (!userId || !tenantId || !threadId || !text?.trim()) {
    console.warn('sendMessage: Missing required parameters');
    return null;
  }

  try {
    const response = await axios.post(
      `${baseUrl}/api/messages/threads/${threadId}/messages`,
      { text: text.trim() },
      {
        headers: buildHeaders(userId, tenantId),
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error) {
    console.error('sendMessage failed:', error?.response?.data || error.message);
    throw error;
  }
}

/**
 * Delete an entire message thread
 * @param {number} threadId - Thread ID
 * @param {object} options - Optional context
 * @returns {Promise<object>} Result
 */
export async function deleteThread(threadId, options = {}) {
  const { userId, tenantId } = getUserContext(options);

  if (!userId || !tenantId || !threadId) {
    console.warn('deleteThread: Missing required parameters');
    return null;
  }

  try {
    const response = await axios.delete(
      `${baseUrl}/api/messages/threads/${threadId}`,
      {
        headers: buildHeaders(userId, tenantId),
        withCredentials: true,
      }
    );

    // Trigger unread count refresh
    window.dispatchEvent(new Event('unread:refresh'));

    return response.data;
  } catch (error) {
    console.error('deleteThread failed:', error?.response?.data || error.message);
    throw error;
  }
}

/**
 * Delete a message (only your own)
 * @param {number} threadId - Thread ID
 * @param {number} messageId - Message ID
 * @param {object} options - Optional context
 * @returns {Promise<object>} Result
 */
export async function deleteMessage(threadId, messageId, options = {}) {
  const { userId, tenantId } = getUserContext(options);

  if (!userId || !tenantId || !threadId || !messageId) {
    console.warn('deleteMessage: Missing required parameters');
    return null;
  }

  try {
    const response = await axios.delete(
      `${baseUrl}/api/messages/threads/${threadId}/messages/${messageId}`,
      {
        headers: buildHeaders(userId, tenantId),
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error) {
    console.error('deleteMessage failed:', error?.response?.data || error.message);
    throw error;
  }
}

/**
 * Mark a thread as read
 * @param {number} threadId - Thread ID
 * @param {object} options - Optional context
 * @returns {Promise<object>} Result
 */
export async function markThreadAsRead(threadId, options = {}) {
  const { userId, tenantId } = getUserContext(options);
  
  if (!userId || !tenantId || !threadId) {
    console.warn('markThreadAsRead: Missing required parameters');
    return null;
  }

  try {
    const response = await axios.post(
      `${baseUrl}/api/messages/threads/${threadId}/read`,
      {},
      {
        headers: buildHeaders(userId, tenantId),
        withCredentials: true,
      }
    );

    // Trigger unread count refresh
    window.dispatchEvent(new Event('unread:refresh'));

    return response.data;
  } catch (error) {
    console.error('markThreadAsRead failed:', error?.response?.data || error.message);
    // Don't throw - marking as read is not critical
    return { success: false };
  }
}

/**
 * Get unread message count
 * @param {object} options - Optional context
 * @returns {Promise<number>} Unread count
 */
export async function getUnreadCount(options = {}) {
  const { userId, tenantId } = getUserContext(options);

  if (!userId || !tenantId) {
    return 0;
  }

  try {
    const response = await axios.get(`${baseUrl}/api/messages/unread-count`, {
      headers: buildHeaders(userId, tenantId),
      withCredentials: true,
    });

    return response.data?.count || 0;
  } catch (error) {
    console.error('getUnreadCount failed:', error?.response?.data || error.message);
    return 0;
  }
}

// =====================================================
// User Search
// =====================================================

/**
 * Search for users to start a conversation with
 * @param {string} query - Search query
 * @param {object} options - Optional context
 * @returns {Promise<object>} { users: [...] }
 */
export async function searchUsers(query, options = {}) {
  if (!query || query.trim().length < 2) {
    return { users: [] };
  }

  const { userId, tenantId } = getUserContext(options);

  if (!tenantId) {
    console.warn('searchUsers: Missing tenantId');
    return { users: [] };
  }

  try {
    const response = await axios.get(`${baseUrl}/api/users/${tenantId}/search`, {
      params: { q: query.trim() },
      headers: buildHeaders(userId, tenantId),
      withCredentials: true,
    });

    return { users: response.data || [] };
  } catch (error) {
    console.error('searchUsers failed:', error?.response?.data || error.message);
    return { users: [] };
  }
}

// =====================================================
// Task Mention Notifications
// =====================================================

/**
 * Notify users mentioned in a task
 * Creates message threads and sends push notifications
 * 
 * @param {object} taskData - Task data for notification
 * @param {number} taskData.taskId - Task ID
 * @param {string} taskData.title - Task title
 * @param {string} taskData.comment - Comment containing @mentions
 * @param {string[]} taskData.mentions - Array of mentioned usernames
 * @param {string} taskData.createdBy - Username of task creator
 * @param {number} taskData.eventId - Event ID
 * @param {object} options - Optional context with tenantId
 * @returns {Promise<object>} Notification result
 */
export async function notifyTaskMentions(taskData, options = {}) {
  const { userId, tenantId } = getUserContext(options);
  
  if (!tenantId) {
    console.warn('notifyTaskMentions: Missing tenantId');
    return null;
  }

  const { taskId, title, comment, mentions, createdBy, eventId } = taskData;

  if (!taskId || !mentions?.length || !eventId) {
    console.warn('notifyTaskMentions: Missing required task data');
    return null;
  }

  try {
    const response = await axios.post(
      `${baseUrl}/api/events/${tenantId}/${eventId}/tasks/mentions`,
      {
        taskId,
        title: title || '',
        comment: comment || '',
        mentions,
        createdBy: createdBy || '',
      },
      {
        headers: buildHeaders(userId, tenantId),
        withCredentials: true,
      }
    );

    // Trigger unread count refresh for all users
    window.dispatchEvent(new Event('unread:refresh'));

    return response.data;
  } catch (error) {
    console.error('notifyTaskMentions failed:', error?.response?.data || error.message);
    throw error;
  }
}

// =====================================================
// Push Notification Subscriptions
// =====================================================

/**
 * Subscribe to push notifications
 * @param {object} subscription - Web Push subscription object
 * @param {object} options - Optional context
 * @returns {Promise<object>} Subscription result
 */
export async function subscribeToPush(subscription, options = {}) {
  const { userId, tenantId } = getUserContext(options);

  if (!userId || !tenantId || !subscription) {
    console.warn('subscribeToPush: Missing required parameters');
    return null;
  }

  try {
    const response = await axios.post(
      `${baseUrl}/api/notifications/subscribe`,
      { 
        subscription, 
        type: 'push',
      },
      {
        headers: buildHeaders(userId, tenantId),
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error) {
    console.error('subscribeToPush failed:', error?.response?.data || error.message);
    throw error;
  }
}

/**
 * Unsubscribe from push notifications
 * @param {string} endpoint - Subscription endpoint to remove
 * @param {object} options - Optional context
 * @returns {Promise<object>} Result
 */
export async function unsubscribeFromPush(endpoint, options = {}) {
  const { userId, tenantId } = getUserContext(options);

  if (!userId || !tenantId || !endpoint) {
    console.warn('unsubscribeFromPush: Missing required parameters');
    return null;
  }

  try {
    const response = await axios.post(
      `${baseUrl}/api/notifications/unsubscribe`,
      { endpoint },
      {
        headers: buildHeaders(userId, tenantId),
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error) {
    console.error('unsubscribeFromPush failed:', error?.response?.data || error.message);
    throw error;
  }
}

/**
 * Get current user information from localStorage
 * @returns {Object|null} User object or null if not found
 */
export function getMe() {
  try {
    const meStr = localStorage.getItem('me');
    if (!meStr) {
      return null;
    }
    
    const me = JSON.parse(meStr);
    
    // Ensure we have at least an ID
    if (!me || (!me.id && !me.userid && !me.userId)) {
      return null;
    }
    
    return me;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
}

// =====================================================
// Exports
// =====================================================

const messagesApi = {
  getThreads,
  getThread,
  createThread,
  deleteThread,
  sendMessage,
  deleteMessage,
  markThreadAsRead,
  getUnreadCount,
  searchUsers,
  notifyTaskMentions,
  subscribeToPush,
  unsubscribeFromPush,
  getMe,
};
export default messagesApi;
